/**
 * ============================================================================
 * ServiceLogPage — 서비스 호출 로그 + SQL 로그 통합 조회
 * ============================================================================
 *
 * 【역할】
 *   백엔드 RPC 호출 내역과 각 요청의 SQL 실행 로그를 통합 조회/분석.
 *   운영/감사/성능 분석용 — serviceName, userId, IP, elapsedMs, SQL 건수 등.
 *   2-Tab 구조: 서비스 로그 / SQL 로그 (전역). 행 클릭 시 Drawer 로 드릴다운.
 *
 * 【주요 기능】
 *   - 검색: 기간(range) + serviceName + userId + status (OK/ERROR)
 *   - Tab 1 (서비스 로그): 요청 ID/서비스/유형/사용자/IP/경과시간/상태
 *   - Tab 2 (SQL 로그): 전역 SQL 실행 로그 (mapperId/sqlType/elapsedMs/sqlText)
 *   - Drawer (드릴다운): 선택 요청의 메타 정보 + 해당 cid/logId 의 SQL 그룹
 *   - SQL 미리보기 100자 + Tooltip 전문 (monospace)
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=log/searchServiceLog) — 서비스 로그 조회
 *   - POST /api/v1/transaction (service=log/searchSqlLog)     — SQL 로그 조회 (cid/logId)
 *
 * 【DataSet/Hook】
 *   - useDataSet<LogRow>()    — listDs      : 서비스 로그 목록 (PK: logId)
 *   - useDataSet<SqlLogRow>() — sqlDs       : Drawer 의 SQL 로그 (FK: cid/logId)
 *   - useDataSet<SqlLogRow>() — globalSqlDs : Tab 2 의 전역 SQL 로그
 *   - useTransaction()        — RPC 통합 호출
 *   - useLabel()              — i18n 라벨(t)
 *
 * 【사용처】 router/index.tsx — `{ path: '/service-log', element: <ServiceLogPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageServiceLog.vue`
 * 【의존성】 antd Drawer/Form/Tabs/Tag/Tooltip/Typography, framework hooks, dayjs
 */
import { useEffect, useMemo, useState } from 'react'
import { Drawer, Form, Tabs, Tag, Tooltip, Typography } from 'antd'
import CrudToolbar from '@/components/common/CrudToolbar'
import SearchPanel from '@/components/common/SearchPanel'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import FormSelect from '@/components/common/FormSelect'
import FormInput from '@/components/common/FormInput'
import { FormDateRangePicker } from '@/components/common/FormDatePicker'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
import type { TrackedRow } from '@/framework/useDataSet'
import { fmtDateTime } from '@/utils/format'
import type { Dayjs } from 'dayjs'

/** 검색 폼 값 — antd Form.useForm 의 getFieldsValue 결과 shape. */
interface SearchFormValues {
  range?: [Dayjs, Dayjs]
  serviceName?: string
  userId?: string
  status?: string
}

/** 서비스 호출 로그 1행. */
interface LogRow {
  /** 로그 PK (DB 시퀀스). */
  logId: string
  /** 요청 ID (단일 HTTP 요청 단위 그룹화 키). */
  requestId: string
  /** Correlation ID (분산 추적용, X-Correlation-Id). */
  cid?: string
  /** 로그 생성 시각 (ISO 8601). */
  createdAt: string
  /** 호출 화면명/위치 (예: "CustomerListPage"). */
  caller?: string
  /** RPC 서비스명 (예: "customer/searchList"). */
  serviceName: string
  /** 로그 유형 (예: "search"·"save"·"login"). */
  logType?: string
  /** 호출 사용자 loginId. */
  userId?: string
  /** 클라이언트 IP. */
  requestIp?: string
  /** 응답 경과 시간 (ms). */
  elapsedMs?: number
  /** 해당 요청에서 실행된 SQL 건수. */
  sqlCount?: number
  /** 누적 영향/조회 행수. */
  rowCount?: number
  /** 처리 상태 ("OK" | "ERROR"). */
  status?: string
  /** 예외 클래스명 (status='ERROR' 시). */
  exceptionClass?: string
}

/** SQL 로그 1행. */
interface SqlLogRow {
  /** SQL 로그 PK. */
  sqlLogId?: string
  /** 요청 내 SQL 일련번호 (1부터). */
  sqlNo?: number
  /** MyBatis Mapper ID (예: "CustomerMapper.searchList"). */
  mapperId: string
  /** SQL 유형 ("SELECT"·"INSERT"·"UPDATE"·"DELETE"·"CALL"). */
  sqlType?: string
  /** SQL 실행 경과 시간 (ms). */
  elapsedMs: number
  /** 영향/조회 행수. */
  rowCount?: number
  /** 실행된 실제 SQL 문자열 (bind parameter 치환 후). */
  sqlText: string
}

/**
 * ServiceLogPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ServiceLogPage() {
  const listDs = useDataSet<LogRow>()
  const sqlDs = useDataSet<SqlLogRow>()
  const globalSqlDs = useDataSet<SqlLogRow>()
  const { transaction } = useTransaction()
  const { t } = useLabel()
  const [searchForm] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<LogRow | null>(null)
  const [activeTab, setActiveTab] = useState<string>('service')

  /**
   * Tab 2 전역 SQL 로그 조회.
   * - 검색 조건의 range 를 startedFrom/startedTo 로 풀어서 전송
   * - cid/logId 미포함 → 전체 SQL 로그
   */
  const searchGlobalSql = () => {
    const v = searchForm.getFieldsValue() as SearchFormValues
    const params: Record<string, unknown> = { ...v }
    if (v.range) {
      params.startedFrom = v.range[0]?.format?.('YYYY-MM-DD HH:mm:ss')
      params.startedTo = v.range[1]?.format?.('YYYY-MM-DD HH:mm:ss')
      delete params.range
    }
    return transaction({
      id: 'searchSqlLogGlobal',
      service: 'log/searchSqlLog',
      inds: { ds_search: params },
      outds: { ds_sql_list: globalSqlDs }
    })
  }

  /**
   * Tab 1 서비스 로그 조회.
   * - 검색 조건의 range 를 startDate/endDate 로 풀어서 전송
   * @param values 외부 폼 값 (생략 시 내부 searchForm)
   */
  const search = (values?: SearchFormValues) => {
    const v = values ?? (searchForm.getFieldsValue() as SearchFormValues)
    const params: Record<string, unknown> = { ...v }
    if (v.range) {
      params.startDate = v.range[0]?.format?.('YYYY-MM-DD HH:mm:ss')
      params.endDate = v.range[1]?.format?.('YYYY-MM-DD HH:mm:ss')
      delete params.range
    }
    return transaction({
      id: 'searchLog',
      service: 'log/searchServiceLog',
      inds: { ds_search: params },
      outds: { ds_list: listDs }
    })
  }

  /**
   * Drawer 드릴다운: 선택 행의 SQL 로그 조회.
   * - 백엔드 listener 는 cid 로 SQL 그룹화 → cid 우선
   * - cid 없으면 logId 로 fallback (백엔드가 cid 역변환)
   * @param row 선택된 LogRow
   */
  const searchDetail = (row: LogRow) => {
    // 백엔드 listener 는 cid 로 SQL 그룹화 → cid 우선, 없으면 logId 로 fallback (백엔드가 cid 역변환)
    return transaction({
      id: 'searchSqlLog',
      service: 'log/searchSqlLog',
      inds: { ds_search: { cid: row.cid, logId: row.logId } },
      outds: { ds_sql_list: sqlDs }
    })
  }

  useEffect(() => {
    search()
  }, [])

  /** 행 클릭 핸들러 — 선택 row state 갱신 + SQL 드릴다운 후 Drawer open. */
  const onRowClick = async (row: LogRow) => {
    setSelectedRow(row)
    await searchDetail(row)
    setDrawerOpen(true)
  }

  const columns = useMemo(() => {
    const specs: ColumnSpec<TrackedRow<LogRow>>[] = [
      {
        key: 'COL_LOG_TIME',
        dataIndex: ['data', 'createdAt'],
        width: 160,
        render: (v) => fmtDateTime(v as string)
      },
      { key: 'COL_REQUEST_ID', dataIndex: ['data', 'requestId'], width: 100 },
      { key: 'COL_SCREEN', dataIndex: ['data', 'caller'], width: 130 },
      { key: 'COL_SERVICE', dataIndex: ['data', 'serviceName'], width: 220 },
      {
        key: 'COL_TYPE',
        dataIndex: ['data', 'logType'],
        width: 80,
        render: (v) => <Tag>{v as string}</Tag>
      },
      { key: 'COL_USER_ID', dataIndex: ['data', 'userId'], width: 100 },
      { key: 'COL_CLIENT_IP', dataIndex: ['data', 'requestIp'], width: 130 },
      { key: 'COL_RESPONSE_MS', dataIndex: ['data', 'elapsedMs'], width: 90, align: 'right' },
      { key: 'COL_SQL_COUNT', dataIndex: ['data', 'sqlCount'], width: 70, align: 'right' },
      { key: 'COL_ROW_COUNT', dataIndex: ['data', 'rowCount'], width: 70, align: 'right' },
      {
        key: 'COL_STATUS',
        dataIndex: ['data', 'status'],
        width: 90,
        render: (v) => (
          <Tag color={v === 'OK' ? 'green' : v === 'ERROR' ? 'red' : 'default'}>{v as string}</Tag>
        )
      },
      { key: 'COL_EXCEPTION', dataIndex: ['data', 'exceptionClass'], width: 180 }
    ]
    return makeColumns(specs, t)
  }, [t])

  const sqlColumns = useMemo(() => {
    const specs: ColumnSpec<TrackedRow<SqlLogRow>>[] = [
      { key: 'LBL_LINE_NO_SHORT', dataIndex: ['data', 'sqlNo'], width: 50, align: 'right' },
      { key: 'COL_MAPPER_ID', dataIndex: ['data', 'mapperId'], width: 240 },
      {
        key: 'COL_SQL_TYPE',
        dataIndex: ['data', 'sqlType'],
        width: 80,
        render: (v) => (v ? <Tag>{v as string}</Tag> : null)
      },
      { key: 'COL_EXEC_MS', dataIndex: ['data', 'elapsedMs'], width: 90, align: 'right' },
      { key: 'COL_ROW_COUNT', dataIndex: ['data', 'rowCount'], width: 70, align: 'right' },
      {
        key: 'LBL_SQL_PREVIEW',
        dataIndex: ['data', 'sqlText'],
        render: (v) => {
          const text = (v as string | undefined) ?? ''
          const preview = text.length > 100 ? text.slice(0, 100) + '…' : text
          return (
            <Tooltip
              overlayStyle={{ maxWidth: 640 }}
              title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>{text}</pre>}
            >
              <Typography.Text style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {preview}
              </Typography.Text>
            </Tooltip>
          )
        }
      }
    ]
    return makeColumns(specs, t)
  }, [t])

  /**
   * Tab 전환 핸들러.
   * - 'sql' Tab 첫 진입 시 전역 SQL 로그 자동 로드
   * - 'service' Tab 은 페이지 진입 시 이미 로드되어 있음
   */
  const onTabChange = (key: string) => {
    setActiveTab(key)
    if (key === 'sql') {
      // 첫 진입 시 로드
      searchGlobalSql()
    }
  }

  return (
    <PageContainer>
      <SearchPanel
        form={searchForm}
        onSearch={(v) => (activeTab === 'sql' ? searchGlobalSql() : search(v))}
      >
        <Form.Item label={t('LBL_PERIOD', '기간')} name="range">
          <FormDateRangePicker showTime />
        </Form.Item>
        <Form.Item label={t('LBL_SERVICE', '서비스')} name="serviceName">
          <FormInput />
        </Form.Item>
        <Form.Item label={t('LBL_USER', '사용자')} name="userId">
          <FormInput />
        </Form.Item>
        <Form.Item label={t('LBL_STATUS', '상태')} name="status">
          <FormSelect
            style={{ width: 120 }}
            options={[
              { label: 'OK', value: 'OK' },
              { label: 'ERROR', value: 'ERROR' }
            ]}
          />
        </Form.Item>
      </SearchPanel>
      <CrudToolbar onSearch={() => (activeTab === 'sql' ? searchGlobalSql() : search())} />
      <div
        className="fw-page-grid"
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={onTabChange}
          items={[
            {
              key: 'service',
              label: t('LBL_SERVICE_LOG', '서비스 로그'),
              children: (
                <DataTable<TrackedRow<LogRow>>
                  rowKey="_rowId"
                  dataSource={listDs.getVisibleRows()}
                  columns={columns}
                  onRow={(row) => ({ onClick: () => onRowClick(row.data) })}
                  scroll={{ x: 1600 }}
                />
              )
            },
            {
              key: 'sql',
              label: t('LBL_SQL_LOG_ALL', 'SQL 로그'),
              children: (
                <DataTable<TrackedRow<SqlLogRow>>
                  rowKey="_rowId"
                  dataSource={globalSqlDs.getVisibleRows()}
                  columns={sqlColumns}
                  scroll={{ x: 1200 }}
                />
              )
            }
          ]}
        />
      </div>
      <Drawer
        title={
          selectedRow
            ? `${t('TTL_REQUEST_DETAIL', '요청 상세')} — ${selectedRow.requestId}`
            : t('TTL_REQUEST_DETAIL', '요청 상세')
        }
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={900}
      >
        {selectedRow && (
          <div style={{ marginBottom: 16 }}>
            <p>
              <b>{t('LBL_SERVICE', '서비스')}:</b> {selectedRow.serviceName}
            </p>
            <p>
              <b>{t('LBL_USER', '사용자')}:</b> {selectedRow.userId} / <b>IP:</b>{' '}
              {selectedRow.requestIp}
            </p>
            <p>
              <b>{t('COL_RESPONSE_MS', '응답시간')}:</b> {selectedRow.elapsedMs}ms / <b>SQL:</b>{' '}
              {selectedRow.sqlCount}
            </p>
            {selectedRow.exceptionClass && (
              <p>
                <b style={{ color: '#cf1322' }}>{t('COL_EXCEPTION', '예외')}:</b>{' '}
                {selectedRow.exceptionClass}
              </p>
            )}
          </div>
        )}
        <h4>{t('LBL_SQL_LOG', 'SQL 로그')}</h4>
        <DataTable<TrackedRow<SqlLogRow>>
          rowKey="_rowId"
          dataSource={sqlDs.getVisibleRows()}
          columns={sqlColumns}
          pagination={false}
          autoHeight={false}
          scroll={{ y: 400 }}
        />
      </Drawer>
    </PageContainer>
  )
}
